import React from 'react';

// Deterministic, colorful initial avatars — the party should feel like
// people, not a list of names. Same uid always gets the same gradient.
const GRADIENTS = [
  ['#BA2BE2', '#7C3AED'],
  ['#FF6B00', '#E0369B'],
  ['#22D3EE', '#7C3AED'],
  ['#FFC107', '#FF6B00'],
  ['#E0369B', '#BA2BE2'],
  ['#10B981', '#22D3EE'],
  ['#F43F5E', '#FF6B00'],
  ['#8B5CF6', '#22D3EE']
];

const SIZES = {
  xs: 'w-5 h-5 text-[9px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-12 h-12 text-base'
};

const hashString = (s) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

export const Avatar = ({ name, uid, photoURL, size = 'md', className = '' }) => {
  const sizeCls = SIZES[size] || SIZES.md;

  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={name || ''}
        className={`${sizeCls} rounded-full object-cover shrink-0 ${className}`}
      />
    );
  }

  const [from, to] = GRADIENTS[hashString(uid || name || '?') % GRADIENTS.length];
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <span
      className={`${sizeCls} rounded-full inline-flex items-center justify-center font-semibold text-white shrink-0 select-none ${className}`}
      style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
      title={name}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
};

// Overlapping avatar cluster with a +N overflow chip
export const AvatarStack = ({ people = [], max = 4, size = 'sm', className = '' }) => {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  if (people.length === 0) return null;

  return (
    <span className={`inline-flex items-center -space-x-1.5 ${className}`}>
      {shown.map((p) => (
        <Avatar
          key={p.uid || p.displayName}
          uid={p.uid}
          name={p.displayName}
          photoURL={p.photoURL}
          size={size}
          className="ring-2 ring-gray-900"
        />
      ))}
      {extra > 0 && (
        <span className={`${SIZES[size] || SIZES.sm} rounded-full bg-gray-700 ring-2 ring-gray-900 inline-flex items-center justify-center text-gray-300 font-semibold`}>
          +{extra > 9 ? '9' : extra}
        </span>
      )}
    </span>
  );
};

export default Avatar;
